import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, input } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, isActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Ripple } from 'primeng/ripple';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LayoutService } from '../../service/app.layout.service';
import { AppMenuItem } from '../models/app-menu-item.model';
import { MenuService } from '../services/app.menu.service';

@Component({
  /* tslint:disable:component-selector */
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-menuitem]',
  templateUrl: './app.menuitem.component.html',
  host: {
    '[class.layout-root-menuitem]': 'root()',
    '[class.active-menuitem]': 'active && !root()',
  },
  styles: [
    `
      /* Replaces the former 'children' animation trigger.
         Only the accordion (inline) submenu animates its height; in the overlay
         layouts the submenu is shown/hidden outright, exactly as before. */
      :host ul.submenu-accordion {
        height: auto;
        overflow: hidden;
        transition: height 400ms cubic-bezier(0.86, 0, 0.07, 1);
      }

      :host ul.submenu-accordion.submenu-collapsed {
        height: 0;
      }

      :host ul.submenu-hidden {
        display: none;
      }

      @media (prefers-reduced-motion: reduce) {
        :host ul.submenu-accordion {
          transition-duration: 1ms;
        }
      }
    `,
  ],
  imports: [Ripple, NgClass, RouterLinkActive, RouterLink, TranslatePipe],
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
  public readonly layoutService = inject(LayoutService);
  public readonly router = inject(Router);
  private readonly menuService = inject(MenuService);

  public readonly item = input.required<AppMenuItem>();
  public readonly index = input.required<number>();
  public readonly root = input<boolean>(false);
  public readonly parentKey = input<string>('');

  public readonly isHorizontal = this.layoutService.isHorizontal;
  public readonly isSlim = this.layoutService.isSlim;
  public readonly isCompact = this.layoutService.isCompact;
  public readonly submenuOverlay = computed(() => {
    return (
      this.layoutService.isDesktop() &&
      (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isCompact())
    );
  });

  public active = false;

  private readonly menuSourceSubscription: Subscription;

  public key: string = '';

  constructor() {
    this.menuSourceSubscription = this.menuService.menuSource$.subscribe((value) => {
      Promise.resolve(null).then(() => {
        if (value.routeEvent) {
          this.active = value.key === this.key || value.key.startsWith(this.key + '-');
        } else if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
          this.active = false;
        }
      });
    });

    this.menuService.resetSource$.subscribe(() => {
      this.active = false;
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      if (this.isSlim() || this.isHorizontal() || this.isCompact()) {
        this.active = false;
      } else if (this.item().routerLink) {
        this.updateActiveStateFromRoute();
      }
    });
  }

  public ngOnInit() {
    const parentKey = this.parentKey();
    this.key = parentKey ? parentKey + '-' + this.index() : String(this.index());

    if (!(this.isSlim() || this.isHorizontal() || this.isCompact()) && this.item().routerLink) {
      this.updateActiveStateFromRoute();
    }
  }

  public ngOnDestroy() {
    if (this.menuSourceSubscription) {
      this.menuSourceSubscription.unsubscribe();
    }
  }

  public updateActiveStateFromRoute() {
    const activeRoute = isActive(this.item().routerLink[0], this.router, {
      paths: 'exact',
      queryParams: 'ignored',
      matrixParams: 'ignored',
      fragment: 'ignored',
    })();

    if (activeRoute) {
      this.active = true;
      //this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
    }
  }

  public itemClick(event: Event) {
    // avoid processing disabled items
    const item = this.item();
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    // navigate with hover
    const root = this.root();
    if ((root && this.isSlim()) || this.isHorizontal() || this.isCompact()) {
      this.layoutService.state.menuHoverActive = !this.layoutService.state.menuHoverActive;
    }

    // execute command
    if (item.command) {
      item.command({ originalEvent: event, item: item });
    }

    // toggle active state
    this.active = !this.active;
    if (item.items) {
      if (root && this.active && (this.isSlim() || this.isHorizontal() || this.isCompact())) {
        this.layoutService.onOverlaySubmenuOpen();
      }
    } else {
      if (this.layoutService.isMobile()) {
        this.layoutService.state.staticMenuMobileActive = false;
      }

      if (this.isSlim() || this.isHorizontal() || this.isCompact()) {
        this.menuService.reset();
        this.layoutService.state.menuHoverActive = false;
      }
    }

    this.menuService.onMenuStateChange({ key: this.key });
  }

  public onMouseEnter() {
    // activate item on hover
    if (this.root() && (this.isSlim() || this.isHorizontal() || this.isCompact()) && this.layoutService.isDesktop()) {
      if (this.layoutService.state.menuHoverActive) {
        this.active = true;
        this.menuService.onMenuStateChange({ key: this.key });
      }
    }
  }

  public get submenuHidden(): boolean {
    return this.submenuOverlay() && !this.active;
  }

  public get submenuCollapsed(): boolean {
    return !this.submenuOverlay() && !this.root() && !this.active;
  }
}
